if (!customElements.get('shipping-bar-component')) {
  class ShippingBarComponent extends HTMLElement {
    constructor() {
      super();
      this.debounceTimer = null;
      
      // Bind methods to maintain 'this' context in event listeners
      this.debouncedRefresh = this.debouncedRefresh.bind(this);
      this.handleThemeEditor = this.handleThemeEditor.bind(this);
    }

    // Called when the element is inserted into the DOM
    connectedCallback() {
      // 1. Target internal DOM elements
      this.bar = this.querySelector('[data-shipping-progress]');
      this.msgContainer = this.querySelector('[data-shipping-msg]');

      // 2. Setup standard event listeners
      document.addEventListener('cart:updated', this.debouncedRefresh);
      document.addEventListener('cart:refresh', this.debouncedRefresh);
      document.addEventListener('shopify:section:load', this.handleThemeEditor);

      // 3. Shopify PubSub integration
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.debouncedRefresh);
      }

      // 4. Initialize global interceptors (only runs once globally)
      this.setupGlobalInterceptors();

      // 5. Initial render
      this.refreshBar();
    }

    // Called when the element is removed from the DOM (e.g., Theme Editor re-renders)
    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.debouncedRefresh);
      document.removeEventListener('cart:refresh', this.debouncedRefresh);
      document.removeEventListener('shopify:section:load', this.handleThemeEditor);
    }

    // --- Attribute Getters ---
    get threshold() { return parseInt(this.getAttribute('threshold'), 10) || 0; }
    get successMsg() { return this.getAttribute('success-msg') || ''; }
    get baseMsg() { return this.getAttribute('base-msg') || ''; }
    get noticeText() { return this.getAttribute('notice-text') || ''; }
    get currency() { return this.getAttribute('currency') || 'USD'; }
    get sectionId() { return this.getAttribute('section-id') || ''; }

    // --- Core Logic ---
    formatCurrency(cents) {
      return (cents / 100).toLocaleString('en-US', { 
        style: 'currency', 
        currency: this.currency 
      });
    }

    debouncedRefresh() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.refreshBar(), 300);
    }

    async refreshBar() {
      try {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${rootUrl}cart.js`);
        
        if (!res.ok) throw new Error('Failed to fetch cart');
        const cart = await res.json();
        const total = cart.total_price;
        
        let percent = this.threshold > 0 ? (total / this.threshold) * 100 : 100;
        percent = Math.min(percent, 100);
        
        if (this.bar) {
          this.bar.style.width = `${percent}%`;
        }
        
        if (this.msgContainer) {
          if (total < this.threshold) {
            const formattedMoney = `<strong>${this.formatCurrency(this.threshold - total)}</strong>`;
            this.msgContainer.innerHTML = this.baseMsg.replace('[amount]', formattedMoney);
          } else {
            const noticeHtml = this.noticeText ? ` <span class="shipping-bar-notice">${this.noticeText}</span>` : '';
            this.msgContainer.innerHTML = this.successMsg + noticeHtml;
          }
        }
      } catch (e) {
        console.error('Shipping bar update failed:', e);
      }
    }

    handleThemeEditor(e) {
      // Only refresh if the section being edited contains this specific bar
      if (e.detail.sectionId === this.sectionId) {
        this.refreshBar();
      }
    }

    // --- Global Cart Interceptors ---
    setupGlobalInterceptors() {
      // Fetch Interceptor
      if (!window.__shippingBarFetchPatched) {
        const originalFetch = window.fetch;
        window.fetch = async function(...args) {
          const response = await originalFetch.apply(this, args);
          const url = args[0];
          if (typeof url === 'string' && url.match(/\/(cart\/(add|update|change|clear))\.js/)) {
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }
          return response;
        };
        window.__shippingBarFetchPatched = true;
      }

      // XHR Interceptor
      if (!window.__shippingBarXHRPatched) {
        const origOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method, url) {
          this.addEventListener('load', function() {
            if (typeof url === 'string' && url.match(/\/(cart\/(add|update|change|clear))\.js/)) {
              document.dispatchEvent(new CustomEvent('cart:updated'));
            }
          });
          origOpen.apply(this, arguments);
        };
        window.__shippingBarXHRPatched = true;
      }
    }
  }

  customElements.define('shipping-bar-component', ShippingBarComponent);
}